import React, { useState, useCallback, useEffect } from 'react'
import QuizContent from '../shared/QuizContent'
import QuizTitle from '../shared/QuizTitle'
import QuizOptions from '../shared/QuizOptions'
import QuizOption from '../shared/QuizOption'
import PlayPauseIcon from '../shared/PlayPauseIcon'
import { QUIZ_OPTIONS_DATA } from '../../constants'
import ssIcon from '../../assets/ss.svg'
import smIcon from '../../assets/sm.svg'
import gfIcon from '../../assets/gf.svg'
import { loadCharacterAttributes } from '../../utils/api.js'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { trackStep5 } from '../../utils/analytics'

const Step4 = ({ onAnswer, onPrevious }) => {
  const [selectedVoice, setSelectedVoice] = useState('')
  const [selectedPersonality, setSelectedPersonality] = useState('')
  const [playingVoice, setPlayingVoice] = useState(null)
  const [attributesMap, setAttributesMap] = useState({})
  const location = useLocation()
  const audioRefs = React.useRef({})
  const isHalloween = !!location.pathname.includes('/halloween')

  const voices = QUIZ_OPTIONS_DATA.voice
  const personalities = QUIZ_OPTIONS_DATA.personality

  useEffect(() => {
    ;(async () => {
      const map = await loadCharacterAttributes()
      setAttributesMap(map)
    })()
  }, [])

  // Auto-select first options on mount
  useEffect(() => {
    if (voices && voices.length > 0 && !selectedVoice) {
      setSelectedVoice(voices[0].value)
    }
  }, [voices])

  useEffect(() => {
    if (personalities && personalities.length > 0 && !selectedPersonality) {
      setSelectedPersonality(personalities[0].value)
    }
  }, [personalities])

  const getAttributeId = (type, label) => {
    if (!attributesMap[type] || !label) return null
    return attributesMap[type][label.trim()] || null
  }

  const handleVoiceClick = useCallback(
    value => {
      setSelectedVoice(value)
      const id = getAttributeId('voice', value)
      console.log('📝 BodyType answer:', value)
      console.log('🔗 API ID:', id)
    },
    [attributesMap],
  )

  const handlePersonalityClick = useCallback(
    value => {
      setSelectedPersonality(value)
      const id = getAttributeId('relationship', value)
      console.log('📝 BodyType answer:', value)
      console.log('🔗 API ID:', id)
    },
    [attributesMap],
  )

  const handlePlayVoice = useCallback(
    (voice, event) => {
      event?.stopPropagation()

      const audio = audioRefs.current[voice.value]
      if (audio) {
        if (playingVoice === voice.value) {
          // Stop playing
          audio.pause()
          audio.currentTime = 0
          setPlayingVoice(null)
        } else {
          // Start playing
          setPlayingVoice(voice.value)
          audio.play()

          audio.onended = () => {
            setPlayingVoice(null)
          }
        }
      }
    },
    [playingVoice],
  )

  const handleContinue = useCallback(() => {
    const voiceOption = voices.find(v => v.value === selectedVoice)

    console.log('Continue clicked with:', {
      voice: selectedVoice,
      voiceAudio: voiceOption?.audio || null,
      personality: selectedPersonality,
    })

    trackStep5(selectedVoice, selectedPersonality)

    onAnswer('step4', {
      voice: selectedVoice,
      voiceAudio: voiceOption?.audio || null,
      personality: selectedPersonality,
    })
  }, [selectedVoice, selectedVoice, selectedPersonality, onAnswer])

  // Allow continue if all selections are made
  const canContinue = selectedVoice && selectedPersonality

  const { t } = useTranslation()

  // Render personality icon
  const renderPersonalityIcon = personalityValue => {
    switch (personalityValue) {
      case 'stepSis':
        return <img src={ssIcon} alt='Step Sister' style={{ width: '60px', height: '60px' }} />
      case 'stepMom':
        return <img src={smIcon} alt='Step Mom' style={{ width: '60px', height: '60px' }} />
      case 'girlfriend':
        return <img src={gfIcon} alt='Girlfriend' style={{ width: '60px', height: '60px' }} />
      default:
        return null
    }
  }

  return (
    <div className='quizContentWrapper'>
      <QuizContent className='step4-design'>
        {/* Voice Block */}
        <div className='step4-block'>
          <QuizTitle>
            {isHalloween ? (
              <>
                {t('choose')}
                <span className='specialText'> {t('voice_text')}</span>
              </>
            ) : (
              t('choose_voice')
            )}
          </QuizTitle>
          <QuizOptions className='voice-options'>
            {voices.map(voice => (
              <QuizOption
                key={voice.value}
                value={voice.value}
                name='voice'
                onClick={handleVoiceClick}
                className={`voice-option ${selectedVoice === voice.value ? 'selected' : selectedVoice ? 'not-selected' : ''}`}
              >
                <div className='voice-card'>
                  <div className='voice-text'>
                    <div className='voice-title'>{t(voice.text)}</div>
                    {/* <div className='voice-icon'>{voice.icon}</div> */}
                  </div>
                  <div className='voice-controls'>
                    <button className='play-button' onClick={e => handlePlayVoice(voice, e)} type='button'>
                      <PlayPauseIcon isPlaying={playingVoice === voice.value} />
                    </button>
                    {/* <div className='voice-waveform'>
                      <VoiceWaveform />
                    </div> */}
                  </div>
                </div>
                <audio ref={el => (audioRefs.current[voice.value] = el)} src={voice.audio} preload='metadata' />
              </QuizOption>
            ))}
          </QuizOptions>
        </div>

        {/* Relationships Block */}
        <div className='step4-block'>
          <QuizTitle>
            {isHalloween ? (
              <>
                {t('choose')}
                <span className='specialText'> {t('relationships_text')}</span>
              </>
            ) : (
              t('choose_relationships')
            )}
          </QuizTitle>
          <QuizOptions className='voice-options'>
            {personalities.map(personality => (
              <QuizOption
                key={personality.value}
                value={personality.value}
                name='personality'
                onClick={handlePersonalityClick}
                className={`personality-option ${selectedPersonality === personality.value ? 'selected' : selectedPersonality ? 'not-selected' : ''}`}
              >
                <div className='personality-item'>
                  <div className='personality-icon'>{renderPersonalityIcon(personality.value)}</div>
                  <div className='personality-info'>
                    <div className='personality-title'>{t(personality.text)}</div>
                  </div>
                </div>
              </QuizOption>
            ))}
          </QuizOptions>
        </div>

        <div className='navigation-wrapper'>
          <button className='previous-button' onClick={onPrevious}>
            {t('previous')}
          </button>
          <button className='continue-button' onClick={handleContinue} disabled={!canContinue}>
            {t('next')}
          </button>
        </div>
      </QuizContent>
    </div>
  )
}

export default Step4
